import React, { useEffect, useState, createContext } from 'react';
import BackButton from '../../components/BackButton';
import BreadCrumbsContainer from '../../components/BreadCrumbs';
import EpicName from '../../components/EpicName';
import InsurancePlansContainer from '../../components/InsurancePlans';
import InsuranceServicesContainer from '../../components/InsuranceServices';
import MedicalGroupsContainer from '../../components/MedicalGroups';
import MentalHealthContainer from '../../components/MentalHealth';
import NetworkContainer from '../../components/Networks';
import PrimaryCareProvidersContainer from '../../components/PrimaryCareProviders';
import OutOfNetworkContainer from '../../components/OutOfNetwork';
import { InsurancePlan, MedicalGroup, NetworkStatus, SelectedNetwork, PlanService, Provider, ContainerSteps, DataResponse } from '../../utils/types';
import { Box, Container, Grid, Typography, CircularProgress } from '@mui/material';
import { ContainerStyles, HeaderTitle, Content, LoadingContainer } from './styles';
import { useFetchData } from '../../utils/hooks';
import { trackMixpanelEvent, EventAction } from '../../mixpanelAnalytics';

export const DataContext = createContext<DataResponse>({} as DataResponse);

const InsuranceChecker = ():JSX.Element => {
    const [ service, setService ] = useState<PlanService>();
    const [ mentalHealth, setMentalHealth ] = useState<NetworkStatus>();
    const [ insurancePlan, setInsurancePlan ] = useState<InsurancePlan>();
    const [ network, setNetwork ] = useState<SelectedNetwork>();
    const [ medicalGroup, setMedicalGroup ] = useState<MedicalGroup>();
    const [ provider, setProvider ] = useState<Provider>();
    const [ containerStep, setContainerStep ] = useState<ContainerSteps>(ContainerSteps.Service);
    const [ loading, setLoading ] = useState(false);
    const [ data, setData ] = useState<DataResponse>();
    const [ servicesOrderMap, setServicesOrderMap ] = useState<{[name: string]: number}>();
    const [ showCrumbs, setShowCrumbs ] = useState(false);

    const getData = async () => {
        try {
            setLoading(true);
            const data = await useFetchData();
            setData(data);
        } catch {

        } finally {
            setLoading(false);
        }
    };

    const getMedicalGroupService = ():PlanService|null => {
        if (!medicalGroup || !service) {
            return null;
        }
        return medicalGroup.services.filter(s => s.name === service.name)[0];
    };

    const nextByNetworkStatus = (status:NetworkStatus, next:ContainerSteps) => {
        if (status === NetworkStatus.InNetwork) {
            setContainerStep(ContainerSteps.InNetwork);
            return;
        }
        if (status === NetworkStatus.OutOfNetwork) {
            setContainerStep(ContainerSteps.OutOfNetwork);
            return;
        }
        if (status === NetworkStatus.AdditionalInfo) {
            setContainerStep(next);
        }
    };

    const checkServiceNetworkStatus = (services:PlanService[]):ContainerSteps|null => {
        if (services.filter(s => s.serviceStatus === NetworkStatus.InNetwork).length === services.length) {
            return ContainerSteps.InNetwork;
        }
        if (services.filter(s => s.serviceStatus === NetworkStatus.OutOfNetwork).length === services.length) {
            return ContainerSteps.OutOfNetwork;
        }
        return null;
    };

    const getEpicName = ():string => {
        if (!insurancePlan) {
            return '';
        }
        if (!medicalGroup) {
            return insurancePlan.epicName;
        }
        const name = medicalGroup.epicNames.find(e => e.insurance === insurancePlan.name);
        if (name) {
            return name.name;
        }
        return 'Find Match or use \'Unlisted HMO\'';
    };

    const handleStartOver = () => {
        setShowCrumbs(false);
        setService(undefined);
        setInsurancePlan(undefined);
        setNetwork(undefined);
        setMentalHealth(undefined);
        setProvider(undefined);
        setMedicalGroup(undefined);
        setContainerStep(ContainerSteps.Plan);
    };

    useEffect(() => {
        trackMixpanelEvent(EventAction.LANDED);
        if (!data) {
            getData();
        }
    }, []);

    useEffect(() => {
        if (provider) {
            setContainerStep(ContainerSteps.InNetwork);
            return;
        }
        const groupService = getMedicalGroupService();
        if (groupService !== null) {
            nextByNetworkStatus(groupService.serviceStatus, ContainerSteps.PCP);
            return;
        }
        if (network?.status === NetworkStatus.SkipGroup) {
            nextByNetworkStatus(NetworkStatus.AdditionalInfo, ContainerSteps.PCP);
        }
        if (network !== undefined) {
            nextByNetworkStatus(network.status, ContainerSteps.MedicalGroup);
            return;
        }
        if (mentalHealth !== undefined) {
            nextByNetworkStatus(mentalHealth, ContainerSteps.Network);
            return;
        }
        // Mental Health can require an additional page.
        // If the plan tells us we need additional information we'll load this page, else just continue onto Networks
        if (service?.name === 'Mental Health' && service?.serviceStatus === NetworkStatus.AdditionalInfo) {
            setContainerStep(ContainerSteps.MentalHealth);
            return;
        }
        if (service) {
            nextByNetworkStatus(service.serviceStatus, ContainerSteps.Network);
            return;
        }
        if (insurancePlan) {
            // Checking to see if all services have the same network status
            // if so, shortcut to in/out of network
            const serviceStatus = checkServiceNetworkStatus(insurancePlan.services);
            if (!serviceStatus) {
                setContainerStep(ContainerSteps.Service);
                setShowCrumbs(true);
                return;
            }
            if (serviceStatus === ContainerSteps.InNetwork) {
                // Set Service to be In Network and populates the name with all services
                // This is because we want to display all services when showing the patient they're in network
                setService({
                    name: insurancePlan.services.map(s => s.name).join(', '),
                    serviceStatus: NetworkStatus.InNetwork,
                });
            }
            setContainerStep(serviceStatus);
            setShowCrumbs(true);
            return;
        }
        if (!insurancePlan) {
            setContainerStep(ContainerSteps.Plan);
            return;
        }
        setContainerStep(ContainerSteps.OutOfNetwork);
    }, [ service, mentalHealth, insurancePlan, network, medicalGroup, provider ]);

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const ip = urlParams.get('ip');
        if (ip && data?.plans?.length) {
            const plan = data.plans.find(p => p.name === ip);
            setInsurancePlan(plan);
        }
    }, [ data?.plans ]);

    useEffect(() => {
        if (data?.services) {
            const map: {[name: string]: number} = {};
            data.services.forEach(s => (map[s.name] = s.order));
            setServicesOrderMap(map);
        }
    }, [ data?.services ]);

    const handleSetInsurancePlan = (plan: InsurancePlan|undefined) => {
        setShowCrumbs(false);
        setInsurancePlan(plan);
        if (plan) {
            trackMixpanelEvent(EventAction.HEALTH_PLAN_SELECTED, { plan: plan.name });
        }
    };
    const handleSetService = (service: PlanService) => {
        setService(service);
        if (service) {
            trackMixpanelEvent(EventAction.SERVICE_SELECTED, { service: service.name });
        }
    };
    const handleSetNetwork = (network: SelectedNetwork) => {
        setNetwork(network);
        if (network) {
            trackMixpanelEvent(EventAction.TYPE_SELECTED, { network: network.network });
        }
    };
    const handleSetMedicalGroup = (medicalGroup: MedicalGroup) => {
        setMedicalGroup(medicalGroup);
        if (medicalGroup) {
            trackMixpanelEvent(EventAction.MEDICAL_GROUP_SELECTED, { medicalGRoup: medicalGroup.name });
        }
    };
    const handleSetProvider = (provider: Provider) => {
        setProvider(provider);
        if (medicalGroup) {
            trackMixpanelEvent(EventAction.PCP_SELECTED, { provider: `${provider.firstName} ${provider.lastName}` });
        }
    };

    const loadContainer = ():JSX.Element => {
        if (!data) {
            return (<div/>);
        }
        switch (containerStep) {
            case ContainerSteps.Plan:
                return (<InsurancePlansContainer handlePlanSelect={handleSetInsurancePlan} handleStartOver={handleStartOver}/>);
            case ContainerSteps.Service:
                return (<InsuranceServicesContainer handleServiceSelect={handleSetService} services={insurancePlan?.services} orderMap={servicesOrderMap}/>);
            case ContainerSteps.MentalHealth:
                return (<MentalHealthContainer handleMentalHealthSelect={setMentalHealth} mentalHealths={insurancePlan?.mentalHealth}/>);
            case ContainerSteps.Network:
                return (<NetworkContainer handleNetworkSelect={handleSetNetwork} plan={insurancePlan}/>);
            case ContainerSteps.MedicalGroup:
                return (<MedicalGroupsContainer handleMedicalGroupSelect={handleSetMedicalGroup} service={service} network={network} handleStartOver={handleStartOver}/>);
            case ContainerSteps.PCP:
                return (<PrimaryCareProvidersContainer handleProviderSelect={handleSetProvider} service={service} network={network} handleStartOver={handleStartOver}/>);
            case ContainerSteps.OutOfNetwork:
                return (<OutOfNetworkContainer service={service} network={network} handleStartOver={handleStartOver}/>);
            case ContainerSteps.InNetwork:
                return (<div/>);
            default:
                return (<div/>);
        }
    };

    if (loading) {
        return (
            <LoadingContainer>
                <CircularProgress sx={{ color: '#FFF' }}/>
            </LoadingContainer>
        );
    }

    const content = loadContainer();
    return (
        <DataContext.Provider value={ data || {} as DataResponse }>
            <Grid container>
                <Grid item xs={12}>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                        }}
                    >
                        <EpicName epicName={getEpicName()}/>
                    </Box>
                </Grid>
            </Grid>
            <Container maxWidth='xl' sx={{ p: '5px', mt: '15px' }}>
                <ContainerStyles>
                    <Grid container>
                        <Grid item xs={12}>
                            <HeaderTitle>
                                <Typography variant="h1">
                                    Check if your insurance is in-network
                                </Typography>
                            </HeaderTitle>
                            <Box sx={{ p: '0px' }} className='fadeIn'>
                                <Content>
                                    <BackButton
                                        network={network}
                                        service={service}
                                        plan={insurancePlan}
                                        step={containerStep}
                                        setStep={setContainerStep}
                                        clearService={() => { setService(undefined); }}
                                        clearInsurancePlan={() => { handleSetInsurancePlan(undefined); }}
                                        clearMentalHealth={() => { setMentalHealth(undefined); }}
                                        clearNetwork={() => { setNetwork(undefined); }}
                                        clearMedicalGroup={() => { setMedicalGroup(undefined); }}
                                        clearProvider={() => { setProvider(undefined); }}
                                    />
                                    <div style={{ width: '100%' }}>
                                        <BreadCrumbsContainer
                                            show={showCrumbs}
                                            service={service}
                                            plan={insurancePlan}
                                            network={network?.network}
                                            group={medicalGroup}
                                            provider={provider}
                                            inNetwork={containerStep === ContainerSteps.InNetwork}
                                            handleStartOver={handleStartOver}
                                        />
                                        {content}
                                    </div>
                                </Content>
                            </Box>
                        </Grid>
                    </Grid>
                </ContainerStyles>
            </Container>
        </DataContext.Provider>
    );
};
export default InsuranceChecker;
